import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Folder, Frame, Layers, MessageSquare, Plus } from "lucide-react";

interface NewMenuProps {
  onNewCanvas: () => void;
  onNewChat: () => void;
  /** Omitted when the browser is scoped to a workspace — no nesting. */
  onNewWorkspace?: () => void;
  /** Create a folder at the active tab's current level (workspace browser). */
  onNewFolder?: () => void;
  isCreating?: boolean;
  /** Disable "New Chat" until the workspace it targets is available. */
  newChatDisabled?: boolean;
}

/**
 * The create items alone — shared by the "+ New" chip and the page's
 * right-click menu, so the two can never offer different verbs.
 */
export function NewMenuItems({
  onNewCanvas,
  onNewChat,
  onNewWorkspace,
  onNewFolder,
  newChatDisabled,
}: Omit<NewMenuProps, "isCreating">) {
  return (
    <>
      {/** Defer so DropdownMenu can finish unmounting; avoids body stuck
       * with pointer-events: none (Radix #3317). Folder/workspace open
       * action-dialog workflows; deferring all four keeps the menu safe
       * regardless of what the parent passes in. */}
      <DropdownMenuItem
        onClick={() => window.setTimeout(() => onNewCanvas(), 0)}
      >
        <Frame className="h-4 w-4" />
        New Canvas
      </DropdownMenuItem>
      <DropdownMenuItem
        onClick={() => window.setTimeout(() => onNewChat(), 0)}
        disabled={newChatDisabled}
      >
        <MessageSquare className="h-4 w-4" />
        New Chat
      </DropdownMenuItem>
      {onNewFolder && (
        <DropdownMenuItem
          onClick={() => window.setTimeout(() => onNewFolder(), 0)}
        >
          <Folder className="h-4 w-4" />
          New Folder
        </DropdownMenuItem>
      )}
      {onNewWorkspace && (
        <DropdownMenuItem
          onClick={() => window.setTimeout(() => onNewWorkspace(), 0)}
        >
          <Layers className="h-4 w-4" />
          New Workspace
        </DropdownMenuItem>
      )}
    </>
  );
}

/** Muted "+ New" chip — creation is routine here, not the loud accent CTA. */
export function NewMenu({
  onNewCanvas,
  onNewChat,
  onNewWorkspace,
  onNewFolder,
  isCreating,
  newChatDisabled,
}: NewMenuProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="secondary" size="sm" disabled={isCreating}>
          <Plus />
          New
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        <NewMenuItems
          onNewCanvas={onNewCanvas}
          onNewChat={onNewChat}
          onNewWorkspace={onNewWorkspace}
          onNewFolder={onNewFolder}
          newChatDisabled={newChatDisabled}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
